import { aoMutarLocalmente, type EstadoDoMotor } from '@sheafyield/sync';
import { useEffect, useRef, useState } from 'react';

import { motor } from '../app/contexto.js';

export interface Consulta<T> {
  /** `undefined` enquanto a leitura corrente não voltou. */
  dados: T | undefined;
  erro: Error | null;
}

/**
 * O tanto do motor que este hook precisa: só o aviso de mudança de fase.
 * Recebê-lo por parâmetro é o mesmo arranjo de `useEstadoDoMotorDe` em
 * `src/app/hooks.ts` — quem testa passa um dublê, sem `vi.mock` do módulo
 * de contexto.
 */
export interface MotorObservavel {
  assinar(ouvinte: (e: EstadoDoMotor) => void): () => void;
}

function comoErro(causa: unknown): Error {
  return causa instanceof Error ? causa : new Error(String(causa));
}

/**
 * Avisa quando um ciclo do motor termina. O motor não emite o sinal de
 * mutação local (esse é de quem escreve: `gravar`), então sem isto uma
 * linha que chegou do servidor ficaria invisível até a próxima escrita.
 * `assinar` chama o ouvinte de imediato com o estado atual; a transição é
 * que interessa, e a primeira chamada não é transição nenhuma.
 */
function aoTerminarCicloDoMotor(
  m: MotorObservavel,
  aviso: () => void,
): () => void {
  let anterior: EstadoDoMotor['fase'] | null = null;
  return m.assinar((estado) => {
    const antes = anterior;
    anterior = estado.fase;
    if (antes === 'sincronizando' && estado.fase !== 'sincronizando') aviso();
  });
}

/**
 * Lê o banco local e continua lendo: a consulta roda ao montar, a cada
 * mutação local e ao fim de cada ciclo do motor. O banco é a fonte da
 * verdade — não há cópia em estado global para desencontrar dele.
 *
 * `chave` é a única dependência de recorte: a função de consulta é fechada
 * sobre o que a tela sabe agora (a data, o id da pessoa) e mudaria de
 * identidade a cada render, o que faria a leitura rodar em laço. Quem
 * quiser reler quando um argumento mudar põe esse argumento na `chave`.
 *
 * Esta é a forma injetável, para teste. As telas usam `useConsulta`.
 */
export function useConsultaCom<T>(
  m: MotorObservavel,
  chave: string,
  consulta: () => Promise<T>,
): Consulta<T> {
  const [resultado, setResultado] = useState<Consulta<T>>({
    dados: undefined,
    erro: null,
  });

  // Chave nova, recorte novo: o que está na tela é da chave anterior e não
  // pode continuar valendo — sem esta limpeza, `dados !== undefined` diria
  // "carregado" enquanto a leitura mostrada é de outra pergunta. O ajuste
  // acontece **durante o render**, e não no efeito, porque é o que o React
  // recomenda para estado que depende de prop (e o que a regra
  // `react-hooks/set-state-in-effect` exige): assim a tela nunca chega a
  // pintar o recorte velho sob a chave nova.
  const [chaveLida, setChaveLida] = useState(chave);
  if (chave !== chaveLida) {
    setChaveLida(chave);
    setResultado({ dados: undefined, erro: null });
  }

  // A consulta mais recente vive numa ref, atualizada depois do render (não
  // durante), para o efeito abaixo poder depender só de `chave`.
  const consultaRef = useRef(consulta);
  useEffect(() => {
    consultaRef.current = consulta;
  });

  useEffect(() => {
    // Esta execução do efeito ainda vale? Uma leitura pendente de uma chave
    // que já mudou (ou de um componente desmontado) tem `geracao` própria,
    // que não a distingue da nova: quem a barra é esta flag.
    let vivo = true;
    // Contador de geração: dentro de uma mesma chave, uma leitura lenta que
    // volta depois de outra mais nova é resposta velha e não pode
    // sobrescrever o que já está na tela.
    let geracao = 0;
    const ler = (): void => {
      const minha = ++geracao;
      const atual = (): boolean => vivo && minha === geracao;
      consultaRef.current().then(
        (dados) => {
          if (atual()) setResultado({ dados, erro: null });
        },
        (causa: unknown) => {
          // Os dados anteriores ficam: uma falha passageira não deve apagar
          // da tela o que já estava certo.
          if (atual())
            setResultado((antes) => ({ ...antes, erro: comoErro(causa) }));
        },
      );
    };
    ler();
    const cancelarMutacao = aoMutarLocalmente(ler);
    const cancelarMotor = aoTerminarCicloDoMotor(m, ler);
    return () => {
      vivo = false;
      cancelarMutacao();
      cancelarMotor();
    };
  }, [m, chave]);

  return resultado;
}

/** `useConsultaCom` amarrado ao motor do app. É o que as telas chamam. */
export function useConsulta<T>(
  chave: string,
  consulta: () => Promise<T>,
): Consulta<T> {
  return useConsultaCom(motor, chave, consulta);
}
